import { useState } from "react";
import { format, addDays } from "date-fns";
import { es } from "date-fns/locale";
import { AlertCircle, MapPin, Phone, Mail, ShoppingBag, MessageSquare, Send, CalendarDays, Clock, User, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/contexts/CartContext";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import FloatingCart from "@/components/cart/FloatingCart";
import { LOCATIONS } from "@/types/orders";
import type { ProductDefinition, SelectedCustomization } from "@/types/orders";
import { getEncargoProducts } from "@/constants/products";
import ProductDetailModal from "@/components/cart/ProductDetailModal";
import { BUSINESS_TIME_SLOTS } from "@/constants/businessHours";

// Cart item structure for encargos
interface EncargoCartItem {
  productId: string;
  productName: string;
  quantity: number;
  selectedExtras: SelectedCustomization[];
  unitPrice: number; // Base price + extras
}

const Encargos = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { addItem, items } = useCart();
  
  const [selectedLocation, setSelectedLocation] = useState("");
  const [phone, setPhone] = useState("");
  const [phoneConfirm, setPhoneConfirm] = useState("");
  const [email, setEmail] = useState("");
  const [emailConfirm, setEmailConfirm] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [observations, setObservations] = useState("");
  const [pickupDate, setPickupDate] = useState<Date | undefined>(undefined);
  const [pickupTime, setPickupTime] = useState("");
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<ProductDefinition | null>(null);
  
  // Local cart for encargos (before sending to main cart)
  const [encargoCart, setEncargoCart] = useState<EncargoCartItem[]>([]);

  // Get encargo products from catalog
  const encargoProducts = getEncargoProducts();

  // Group products by subcategory
  const productsBySubcategory = encargoProducts.reduce((acc, product) => {
    const subcategory = product.subcategory || "Otros";
    if (!acc[subcategory]) acc[subcategory] = [];
    acc[subcategory].push(product);
    return acc;
  }, {} as Record<string, ProductDefinition[]>);

  // Get total items in local encargo cart
  const totalEncargoItems = encargoCart.reduce((sum, item) => sum + item.quantity, 0);
  const totalEncargoPrice = encargoCart.reduce((sum, item) => sum + (item.unitPrice * item.quantity), 0);

  // Get items in cart for a specific product
  const getCartCountForProduct = (productId: string): number => {
    return encargoCart
      .filter(item => item.productId === productId)
      .reduce((sum, item) => sum + item.quantity, 0);
  };

  // Get existing cart item for a product (for editing)
  const getExistingCartItem = (productId: string): EncargoCartItem | undefined => {
    return encargoCart.find(item => item.productId === productId);
  };

  // Handle product click - open modal with existing data if editing
  const handleProductClick = (product: ProductDefinition) => {
    setSelectedProduct(product);
    setModalOpen(true);
  };

  // Handle add/update to cart from modal
  const handleAddFromModal = (quantity: number, selectedOptions: SelectedCustomization[]) => {
    if (!selectedProduct) return;

    const extrasTotal = selectedOptions.reduce((sum, opt) => sum + opt.price, 0);
    const unitPrice = selectedProduct.price + extrasTotal;

    // Update or add to local encargo cart
    setEncargoCart(prev => {
      // Check if same product exists (for editing we replace entirely)
      const existingIndex = prev.findIndex(item => item.productId === selectedProduct.id);

      if (existingIndex >= 0) {
        // Replace existing item with new quantity and options
        const updated = [...prev];
        updated[existingIndex] = {
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          quantity,
          selectedExtras: selectedOptions,
          unitPrice,
        };
        return updated;
      } else {
        // Add new item
        return [...prev, {
          productId: selectedProduct.id,
          productName: selectedProduct.name,
          quantity,
          selectedExtras: selectedOptions,
          unitPrice,
        }];
      }
    });

    toast({
      title: "✓ Añadido",
      description: `${quantity}x ${selectedProduct.name}`,
    });
  };

  // Handle remove from cart
  const handleRemoveFromCart = (productId: string) => {
    setEncargoCart(prev => prev.filter(item => item.productId !== productId));
    toast({
      title: "🗑️ Eliminado",
      description: "Producto eliminado del pedido",
    });
  };

  // Use centralized business time slots
  const timeSlots = BUSINESS_TIME_SLOTS;

  // Minimum date is 7 days from now
  const minDate = addDays(new Date(), 7);

  const handleAddToCart = () => {
    // Validate form
    if (!selectedLocation) {
      toast({
        title: "Error",
        description: "Por favor, selecciona una ubicación de recogida.",
        variant: "destructive",
      });
      return;
    }

    if (!customerName.trim()) {
      toast({
        title: "Error",
        description: "Por favor, introduce tu nombre.",
        variant: "destructive",
      });
      return;
    }

    if (!phone.trim()) {
      toast({
        title: "Error",
        description: "Por favor, introduce tu número de teléfono.",
        variant: "destructive",
      });
      return;
    }

    if (phone !== phoneConfirm) {
      toast({
        title: "Error",
        description: "Los números de teléfono no coinciden.",
        variant: "destructive",
      });
      return;
    }

    if (!email.trim()) {
      toast({
        title: "Error",
        description: "Por favor, introduce tu correo electrónico.",
        variant: "destructive",
      });
      return;
    }

    if (email !== emailConfirm) {
      toast({
        title: "Error",
        description: "Los correos electrónicos no coinciden.",
        variant: "destructive",
      });
      return;
    }

    if (!pickupDate) {
      toast({
        title: "Error",
        description: "Por favor, selecciona una fecha de recogida.",
        variant: "destructive",
      });
      return;
    }

    if (!pickupTime) {
      toast({
        title: "Error",
        description: "Por favor, selecciona una hora de recogida.",
        variant: "destructive",
      });
      return;
    }

    if (encargoCart.length === 0) {
      toast({
        title: "Error",
        description: "Por favor, selecciona al menos un producto.",
        variant: "destructive",
      });
      return;
    }

    // Add all items from encargo cart to main cart
    console.log('🔥 INICIO: Procesando encargo para checkout');
    console.log('📦 encargoCart completo:', JSON.stringify(encargoCart, null, 2));
    
    encargoCart.forEach((item, index) => {
      console.log(`\n🎂 Procesando item ${index + 1}:`, item.productName);
      console.log('  💰 Precio unitario (con extras):', item.unitPrice);
      console.log('  📊 Cantidad:', item.quantity);
      console.log('  🎨 selectedExtras:', JSON.stringify(item.selectedExtras, null, 2));
      
      // Convert selectedExtras to legacy removedIngredients format for compatibility
      // Plus store ALL extras data for Stripe description (not just paid ones)
      const removedIngredients = item.selectedExtras
        .filter(ext => ext.price === 0)
        .map(ext => ext.optionLabel);
      
      const paidExtras = item.selectedExtras.filter(e => e.price > 0);
      const extrasTotal = paidExtras.reduce((sum, e) => sum + e.price, 0);
      const basePrice = item.unitPrice - extrasTotal;
      
      console.log('  ✂️ removedIngredients (gratis):', removedIngredients);
      console.log('  💵 paidExtras:', paidExtras);
      console.log('  📐 Cálculo: unitPrice', item.unitPrice, '- extrasTotal', extrasTotal, '= basePrice', basePrice);

      for (let i = 0; i < item.quantity; i++) {
        const cartItem = {
          id: `encargo-${item.productId}-${Date.now()}-${i}`,
          name: item.productName,
          price: item.unitPrice, // Total price including extras
          category: "Tarta por Encargo",
          removedIngredients: removedIngredients.length > 0 ? removedIngredients : undefined,
          // Pass ALL extras for Stripe description (free + paid)
          selectedExtras: item.selectedExtras.length > 0 ? item.selectedExtras : undefined,
          basePrice: basePrice,
        };
        console.log(`  ➕ Añadiendo al carrito (${i + 1}/${item.quantity}):`, JSON.stringify(cartItem, null, 2));
        addItem(cartItem);
      }
    });
    
    console.log('✅ FIN: Todos los items añadidos al carrito principal');

    // Store encargo details in sessionStorage for checkout
    const encargoDetails = {
      location: selectedLocation,
      customerName,
      phone,
      email,
      pickupDate: format(pickupDate, "yyyy-MM-dd"),
      pickupTime,
      observations,
      products: encargoCart,
    };
    sessionStorage.setItem("encargoDetails", JSON.stringify(encargoDetails));

    toast({
      title: "¡Productos añadidos!",
      description: "Continúa al checkout para finalizar tu encargo.",
    });

    navigate("/checkout");
  };

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-card py-8 md:py-12 border-b border-border">
        <div className="container mx-auto px-4 text-center">
          <h1 className="font-serif text-3xl md:text-5xl font-bold text-primary mb-3">
            Encargos Personalizados
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-sm md:text-base">
            Haz tu pedido con antelación y lo tendremos listo para cuando lo necesites.
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-8 md:py-12 bg-background pb-32">
        <div className="container mx-auto px-4 max-w-3xl">
          {/* Warning */}
          <Alert className="mb-6 border-secondary bg-secondary/10">
            <AlertCircle className="h-5 w-5 text-secondary" />
            <AlertDescription className="text-primary font-medium">
              Los encargos deben realizarse con al menos <strong>una semana de antelación</strong>.
            </AlertDescription>
          </Alert>

          <div className="space-y-6">
            {/* Location Selection */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <MapPin className="h-5 w-5 text-secondary" />
                  Ubicación de Recogida
                </CardTitle>
              </CardHeader>
              <CardContent>
                <RadioGroup
                  value={selectedLocation}
                  onValueChange={setSelectedLocation}
                  className="space-y-2"
                >
                  {LOCATIONS.map((location) => (
                    <div
                      key={location.id}
                      className={`flex items-start space-x-3 p-4 rounded-xl border transition-all cursor-pointer active:scale-[0.98] ${
                        selectedLocation === location.id
                          ? "border-secondary bg-secondary/10"
                          : "border-border hover:bg-muted/30"
                      }`}
                      onClick={() => setSelectedLocation(location.id)}
                    >
                      <RadioGroupItem value={location.id} id={location.id} className="mt-1" />
                      <Label htmlFor={location.id} className="flex-1 cursor-pointer">
                        <span className="font-medium text-primary block">{location.name}</span>
                        <span className="text-sm text-muted-foreground block mt-0.5">{location.address}</span>
                        <span className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                          <Phone className="h-3 w-3" />
                          {location.phone}
                        </span>
                      </Label>
                    </div>
                  ))}
                </RadioGroup>
              </CardContent>
            </Card>

            {/* Customer Name */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <User className="h-5 w-5 text-secondary" />
                  Nombre
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Input
                  placeholder="Tu nombre completo"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  className="text-base h-12"
                />
              </CardContent>
            </Card>

            {/* Contact Info - Both Required */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <Phone className="h-5 w-5 text-secondary" />
                  Datos de Contacto
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Phone Fields */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Phone className="h-4 w-4 text-secondary" />
                    Teléfono <span className="text-destructive">*</span>
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="tel"
                      placeholder="Ej: 600 123 456"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="text-base h-12"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm text-muted-foreground">Repetir teléfono</Label>
                    <Input
                      type="tel"
                      placeholder="Repite tu teléfono"
                      value={phoneConfirm}
                      onChange={(e) => setPhoneConfirm(e.target.value)}
                      className={`text-base h-12 ${phone && phoneConfirm && phone !== phoneConfirm ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                    />
                    {phone && phoneConfirm && phone !== phoneConfirm && (
                      <p className="text-sm text-destructive mt-1">Los teléfonos no coinciden</p>
                    )}
                  </div>
                </div>

                {/* Email Fields */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium text-primary">
                    <Mail className="h-4 w-4 text-secondary" />
                    Correo electrónico <span className="text-destructive">*</span>
                  </div>
                  <div className="space-y-1">
                    <Input
                      type="email"
                      placeholder="Ej: tu@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="text-base h-12"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-sm text-muted-foreground">Repetir correo electrónico</Label>
                    <Input
                      type="email"
                      placeholder="Repite tu email"
                      value={emailConfirm}
                      onChange={(e) => setEmailConfirm(e.target.value)}
                      className={`text-base h-12 ${email && emailConfirm && email !== emailConfirm ? 'border-destructive focus-visible:ring-destructive' : ''}`}
                    />
                    {email && emailConfirm && email !== emailConfirm && (
                      <p className="text-sm text-destructive mt-1">Los correos no coinciden</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Date and Time Selection */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <CalendarDays className="h-5 w-5 text-secondary" />
                  Fecha y Hora de Recogida
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Fecha</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start text-left font-normal h-12"
                      >
                        <CalendarDays className="mr-2 h-4 w-4 text-secondary" />
                        {pickupDate ? (
                          format(pickupDate, "EEEE, d 'de' MMMM", { locale: es })
                        ) : (
                          <span className="text-muted-foreground">Selecciona una fecha</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={setPickupDate}
                        disabled={(date) => date < minDate}
                        initialFocus
                        locale={es}
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground mb-2 block">Hora</Label>
                  <Select value={pickupTime} onValueChange={setPickupTime}>
                    <SelectTrigger className="h-12">
                      <Clock className="mr-2 h-4 w-4 text-secondary" />
                      <SelectValue placeholder="Selecciona una hora" />
                    </SelectTrigger>
                    <SelectContent>
                      {timeSlots.map((time) => (
                        <SelectItem key={time} value={time}>
                          {time}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Product Selection - New Card Style */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <ShoppingBag className="h-5 w-5 text-secondary" />
                  Tartas por Encargo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <p className="text-sm text-muted-foreground">
                  Pulsa en una tarta para ver opciones de personalización y añadir al pedido.
                </p>

                {/* Products by subcategory */}
                {Object.entries(productsBySubcategory).map(([subcategory, products]) => (
                  <div key={subcategory} className="space-y-3">
                    <h3 className="font-semibold text-primary text-sm uppercase tracking-wide">
                      {subcategory}
                    </h3>
                    <div className="space-y-2">
                      {products.map((product) => {
                        const cartCount = getCartCountForProduct(product.id);
                        const hasOptions = product.customizationGroups && product.customizationGroups.length > 0;

                        return (
                          <button
                            key={product.id}
                            onClick={() => handleProductClick(product)}
                            className={`w-full text-left p-4 rounded-xl border-2 transition-all active:scale-[0.98] ${
                              cartCount > 0
                                ? "border-secondary bg-secondary/10"
                                : "border-border hover:border-muted-foreground/30 bg-card"
                            }`}
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className={`font-medium ${cartCount > 0 ? "text-primary" : "text-foreground"}`}>
                                    {product.name}
                                  </span>
                                  {cartCount > 0 && (
                                    <Badge className="bg-secondary text-secondary-foreground">
                                      {cartCount}
                                    </Badge>
                                  )}
                                </div>
                                {product.description && (
                                  <p className="text-sm text-muted-foreground mt-1 line-clamp-1">
                                    {product.description}
                                  </p>
                                )}
                                {hasOptions && (
                                  <span className="text-xs text-orange-600 mt-1 inline-block">
                                    ⚙️ Personalizable
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                {product.price > 0 && (
                                  <span className="text-sm font-medium text-muted-foreground">
                                    {product.price.toFixed(2)}€
                                  </span>
                                )}
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}

                {/* Cart summary */}
                {totalEncargoItems > 0 && (
                  <div className="pt-4 border-t border-border space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-primary">
                        {totalEncargoItems} tarta{totalEncargoItems !== 1 ? "s" : ""} seleccionada{totalEncargoItems !== 1 ? "s" : ""}
                      </span>
                      {totalEncargoPrice > 0 && (
                        <span className="font-bold text-secondary">
                          {totalEncargoPrice.toFixed(2)}€
                        </span>
                      )}
                    </div>
                    {/* Show selected items */}
                    <div className="space-y-2">
                      {encargoCart.map((item, idx) => (
                        <div key={idx} className="flex items-start justify-between text-sm bg-muted/50 p-3 rounded-lg">
                          <div>
                            <span className="font-medium">{item.quantity}x {item.productName}</span>
                            {item.selectedExtras.length > 0 && (
                              <div className="text-xs text-muted-foreground mt-1">
                                {item.selectedExtras.map(e => e.optionLabel).join(", ")}
                              </div>
                            )}
                          </div>
                          {item.unitPrice > 0 && (
                            <span className="text-muted-foreground">
                              {(item.unitPrice * item.quantity).toFixed(2)}€
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Observations */}
            <Card className="border-border">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-primary text-lg">
                  <MessageSquare className="h-5 w-5 text-secondary" />
                  Observaciones
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  placeholder="Tamaños, dedicatorias, modificaciones adicionales..."
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={3}
                  className="resize-none text-base"
                />
              </CardContent>
            </Card>

            {/* Submit Button */}
            <Button
              size="lg"
              className="w-full h-14 text-lg bg-secondary hover:bg-secondary/90 text-secondary-foreground"
              onClick={handleAddToCart}
              disabled={
                encargoCart.length === 0 ||
                !phone.trim() ||
                !phoneConfirm.trim() ||
                phone !== phoneConfirm ||
                !email.trim() ||
                !emailConfirm.trim() ||
                email !== emailConfirm ||
                !customerName.trim() ||
                !selectedLocation ||
                !pickupDate ||
                !pickupTime
              }
            >
              <Send className="h-5 w-5 mr-2" />
              Pagar ahora
              {totalEncargoPrice > 0 && ` • ${totalEncargoPrice.toFixed(2)}€`}
            </Button>

            <p className="text-center text-xs text-muted-foreground">
              Te contactaremos para confirmar el precio final según tamaños y personalizaciones.
            </p>
          </div>
        </div>
      </section>

      <FloatingCart />
      
      {/* Product Detail Modal with editing support */}
      <ProductDetailModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedProduct(null);
        }}
        onAddToCart={handleAddFromModal}
        onRemoveFromCart={handleRemoveFromCart}
        product={selectedProduct}
        existingQuantity={selectedProduct ? (getExistingCartItem(selectedProduct.id)?.quantity || 0) : 0}
        existingOptions={selectedProduct ? (getExistingCartItem(selectedProduct.id)?.selectedExtras || []) : []}
      />
    </Layout>
  );
};

export default Encargos;
